import React, {useEffect, useRef, useState} from 'react';
import {TouchableOpacity} from 'react-native';
import {useIsFocused} from '@react-navigation/native';
import styled from 'styled-components/native';
import {Track} from 'react-native-track-player';
import Icon from 'react-native-vector-icons/FontAwesome';
import Centered from '../../styled-components/Centered';
import Screen from '../../styled-components/Screen';
import {withContentRevalidation} from '../../utils/contentAssetUrl';

const PlayButton = styled(TouchableOpacity)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 150px;
  height: 150px;
  margin-bottom: 16px;
  background: white;
  border-radius: 100px;
  border-width: 15px;
  border-color: rgb(196, 206, 240);
`;

interface AudioProps {
  source: Track;
}

const AUDIO_REVALIDATION_DELAY = 4000;

const Audio = ({source}: AudioProps) => {
  const pageFocus = useIsFocused();
  const audioRef = useRef<HTMLAudioElement>(null);
  const loadToken = useRef(Date.now().toString()).current;
  const [revalidationAttempt, setRevalidationAttempt] = useState(0);
  const [playing, setPlaying] = useState(false);
  const audioUrl = withContentRevalidation(
    String(source.url),
    loadToken,
    revalidationAttempt,
  );

  const resetAudio = () => {
    setPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  useEffect(() => resetAudio(), [pageFocus]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const audio = audioRef.current;

      // Replacing src reloads the media element. Only do that before playback
      // starts so the CDN retry cannot interrupt or rewind the listener.
      if (!audio || (audio.paused && audio.currentTime === 0)) {
        setRevalidationAttempt(1);
      }
    }, AUDIO_REVALIDATION_DELAY);

    return () => window.clearTimeout(timer);
  }, [source.url]);

  const handlePlayPressed = () => {
    switch (playing) {
      case true:
        setPlaying(false);
        audioRef.current && audioRef.current.pause();
        break;
      case false:
        setPlaying(true);
        audioRef.current && audioRef.current.play();
        break;
    }
  };

  return (
    <Screen>
      <Centered>
        <PlayButton onPress={handlePlayPressed}>
          <Icon
            name={playing ? 'pause' : 'play'}
            size={40}
            color={'rgb(95, 105, 142)'}
          />
        </PlayButton>
        <audio
          controls
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}></audio>
      </Centered>
    </Screen>
  );
};

export default Audio;
